/**
 * StoryProgress — engaging, phase-by-phase progress panel shown while
 * test cases are being generated from a user story.
 *
 * The backend call is a single request (no streaming), so this advances
 * through the real phases on a timer and holds on the final phase until
 * the parent flips `active` to false (i.e. the response has arrived).
 *
 * Props:
 *   active           — boolean: true while generating
 *   hasScreenshots   — boolean: include the screenshot-analysis phase
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Sparkles, Lightbulb } from 'lucide-react';

const TIPS = [
  'Clear acceptance criteria produce sharper test cases.',
  'You can edit any generated step before saving.',
  'Add UI screenshots to help AI detect exact field names.',
  'Generated tests cover happy paths, edge cases, and errors.',
  'Turn on self-healing so tests survive selector changes.',
];

function buildPhases(hasScreenshots) {
  const phases = [
    { key: 'read',    label: 'Reading your user story',        hint: 'Parsing intent and acceptance criteria' },
  ];
  if (hasScreenshots) {
    phases.push({ key: 'vision', label: 'Analyzing UI screenshots', hint: 'Extracting fields, buttons, and labels' });
  }
  phases.push(
    { key: 'scenarios', label: 'Identifying test scenarios',      hint: 'Mapping user flows to verify' },
    { key: 'edge',      label: 'Covering edge & negative cases',  hint: 'Boundary values and error handling' },
    { key: 'steps',     label: 'Writing step-by-step actions',    hint: 'Concrete steps with expected results' },
    { key: 'finalize',  label: 'Finalizing test cases',          hint: 'Structuring and validating output' },
  );
  return phases;
}

export default function StoryProgress({ active, hasScreenshots = false }) {
  const phases = buildPhases(hasScreenshots);
  const [current, setCurrent] = useState(0);
  const [tip, setTip] = useState(0);
  const timerRef = useRef(null);
  const tipRef = useRef(null);

  // Advance through phases, but hold on the last one until the request finishes.
  useEffect(() => {
    if (!active) {
      setCurrent(0);
      return;
    }
    setCurrent(0);
    const perPhase = hasScreenshots ? 2200 : 1900; // ms
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c < phases.length - 1 ? c + 1 : c));
    }, perPhase);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hasScreenshots]);

  // Rotate tips
  useEffect(() => {
    if (!active) return;
    tipRef.current = setInterval(() => {
      setTip((t) => (t + 1) % TIPS.length);
    }, 3500);
    return () => clearInterval(tipRef.current);
  }, [active]);

  if (!active) return null;

  const pct = Math.round(((current + 1) / phases.length) * 100);

  return (
    <div className="glass-card border border-white/5 rounded-xl p-6" data-testid="story-progress">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 relative flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#D946EF] to-[#00E599] rounded-full animate-spin" />
          <div className="absolute inset-[3px] bg-[#0B0F1A] rounded-full flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#00E599]" />
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Generating your test cases
          </h3>
          <p className="text-xs text-slate-400">This usually takes 15–40 seconds</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-5">
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#D946EF] to-[#00E599]"
            initial={{ width: '0%' }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Phase checklist */}
      <div className="space-y-3">
        {phases.map((phase, idx) => {
          const done = idx < current;
          const activePhase = idx === current;
          return (
            <div
              key={phase.key}
              className={`flex items-start gap-3 transition-opacity ${
                idx > current ? 'opacity-40' : 'opacity-100'
              }`}
            >
              <div className="mt-0.5 flex-shrink-0">
                {done ? (
                  <div className="w-5 h-5 rounded-full bg-[#00E599]/15 border border-[#00E599]/40 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#00E599]" />
                  </div>
                ) : activePhase ? (
                  <div className="w-5 h-5 rounded-full bg-[#00E599]/10 border border-[#00E599]/30 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 text-[#00E599] animate-spin" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border border-white/15" />
                )}
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-medium ${activePhase ? 'text-white' : done ? 'text-slate-300' : 'text-slate-500'}`}>
                  {phase.label}
                </p>
                {activePhase && (
                  <p className="text-xs text-slate-500 mt-0.5">{phase.hint}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Rotating tip */}
      <div className="mt-6 pt-4 border-t border-white/5">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-[#D946EF] flex-shrink-0 mt-0.5" />
          <AnimatePresence mode="wait">
            <motion.p
              key={tip}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3 }}
              className="text-xs text-slate-400"
            >
              {TIPS[tip]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
